/**
 * Version checking utility to surface (and, for forced deployments, drive)
 * app updates when new versions are deployed.
 *
 * Anchor semantics: the update check compares the SERVER manifest
 * (/version.json, written by scripts/generate-version.js) against the
 * RUNNING bundle's own embedded identity (src/buildInfo.json, written by
 * scripts/generate-build-id.js in the prebuild step and compiled into the
 * bundle). An update is pending exactly while the server identifies a
 * different build than the one executing, which gives three properties by
 * construction:
 *
 * - After a successful reload the fresh bundle carries the new identity, so
 *   the comparison turns false and no further reload can trigger. There is
 *   no stored baseline to advance, corrupt, or get stuck on.
 * - A dismissed banner resurfaces on every later check until the running
 *   bundle actually changes, so an update can never be silenced by accident.
 * - Detection needs NO web storage, so storage denial can never hide an
 *   update or fake one.
 *
 * The identity comparison uses the git hash when both sides carry one and
 * falls back to the package version otherwise. Timestamps are deliberately
 * NOT compared: the prebuild and postbuild steps stamp different times for
 * the same deploy.
 *
 * Web storage is used ONLY to dedupe AUTOMATIC forced reloads:
 * sessionStorage (survives our own reload, resets on a real navigation),
 * degrading to an in-memory record plus a navigation-type gate when
 * sessionStorage is denied: without a durable record, an automatic reload
 * is allowed only when navigation timing positively confirms the page is a
 * fresh navigation; a post-reload page, or an indeterminate signal, falls
 * back to the persistent prompt. See shouldAutoReload for the bounds.
 *
 * Cross-tab note: tabs are deliberately independent; nothing here relies on
 * cross-tab atomicity. Two open tabs may each perform one automatic forced
 * reload for the same deployment, which is harmless, and a tab that slept
 * through a deployment re-evaluates running-vs-server on its next poll, so
 * no tab can be left permanently stuck by another tab's state.
 */

export interface VersionInfo {
  version: string;
  timestamp: string;
  gitHash?: string;
  buildId?: string;
  forceUpdate?: boolean;
}

export interface RunningVersion {
  version: string;
  timestamp?: string;
  gitHash?: string;
  buildId?: string;
}

export interface UpdateCheckResult {
  updateAvailable: boolean;
  forceUpdate: boolean;
  pendingVersion: VersionInfo | null;
}

export interface UpdatePrompt {
  pendingVersion: VersionInfo;
  forced: boolean;
}

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const FORCED_RELOAD_SESSION_KEY = 'app-forced-reload-attempts';
// Hard cap on automatic reloads per tab session. Once reached, every further
// forced deployment falls back to the persistent prompt, so even endless
// manifest churn (A to B to A, new timestamps, and so on) cannot turn into
// endless reloads.
const MAX_AUTO_RELOADS_PER_SESSION = 5;

// In-memory fallback: storage denial must never disable update handling,
// so web-storage reads/writes degrade to module state.
let inMemoryReloadAttempts: string[] = [];

/** Test-only: reset module state between tests. */
export function resetVersionCheckStateForTests(): void {
  inMemoryReloadAttempts = [];
}

/**
 * The identity of the bundle that is actually executing, embedded at build
 * time. Returns null when no identity was embedded (development), in which
 * case there is nothing safe to compare and checks report no update;
 * production builds always embed one via the prebuild step.
 */
export function getRunningVersion(): RunningVersion | null {
  try {
    // Written by scripts/generate-build-id.js before every build.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const info = require('../buildInfo.json');
    if (info && typeof info.version === 'string') {
      return info as RunningVersion;
    }
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Fetch the current version from the server
 */
export async function fetchServerVersion(): Promise<VersionInfo | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      console.warn('Failed to fetch version info');
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching version:', error);
    return null;
  }
}

/**
 * True when the server manifest identifies a different build than the one
 * running: git hashes when both are known, package version otherwise.
 */
function isDifferentBuild(server: VersionInfo, running: RunningVersion): boolean {
  const serverHash = (server.gitHash || '').trim();
  const runningHash = (running.gitHash || '').trim();
  if (serverHash && runningHash) {
    return serverHash !== runningHash;
  }
  return server.version !== running.version;
}

/** Stable identity string for a target deployment, used to dedupe reloads. */
function targetIdentity(server: VersionInfo): string {
  const hash = (server.gitHash || '').trim();
  if (hash) {
    return `${server.version}-${hash}`;
  }
  return `${server.version}|${server.timestamp}`;
}

/** Attempts recorded this tab session: sessionStorage plus in-memory union. */
function getReloadAttempts(): string[] {
  let stored: string[] = [];
  try {
    const raw = window.sessionStorage.getItem(FORCED_RELOAD_SESSION_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) {
      stored = parsed.filter((item): item is string => typeof item === 'string');
    }
  } catch (error) {
    // Storage denied or corrupt: the in-memory record still applies.
  }
  const merged = [...stored];
  for (const identity of inMemoryReloadAttempts) {
    if (!merged.includes(identity)) {
      merged.push(identity);
    }
  }
  return merged;
}

/**
 * Persist the attempt list. Returns true only when the record was durably
 * written to sessionStorage (verified by read-back), meaning the dedup will
 * survive the reload itself.
 */
function persistReloadAttempts(attempts: string[]): boolean {
  try {
    const payload = JSON.stringify(attempts);
    window.sessionStorage.setItem(FORCED_RELOAD_SESSION_KEY, payload);
    return window.sessionStorage.getItem(FORCED_RELOAD_SESSION_KEY) === payload;
  } catch (error) {
    return false;
  }
}

type NavigationLoadType = 'reload' | 'not-reload' | 'unknown';

/**
 * Classify how this page load happened. Used as the storage-free brake:
 * when sessionStorage is denied the reload dedup cannot survive a reload,
 * so the navigation type is the only signal that can bound a chain.
 *
 * Prefers the Navigation Timing API, falling back to the deprecated but
 * still widely implemented performance.navigation (type 1 is TYPE_RELOAD).
 * Returns 'unknown' only when NEITHER carries a signal; callers must treat
 * 'unknown' as unsafe, because "cannot determine" is exactly the state a
 * post-reload page would be in if the signal were unavailable, so reading
 * it as permission would allow an unbounded reload chain.
 */
function classifyCurrentLoad(): NavigationLoadType {
  try {
    if (window.performance && typeof window.performance.getEntriesByType === 'function') {
      const entries = window.performance.getEntriesByType('navigation');
      const navigation = entries && (entries[0] as PerformanceNavigationTiming | undefined);
      if (navigation && typeof navigation.type === 'string') {
        return navigation.type === 'reload' ? 'reload' : 'not-reload';
      }
    }
    const legacyNavigation = window.performance &&
      (window.performance as Performance & {
        navigation?: { type?: number; TYPE_RELOAD?: number };
      }).navigation;
    if (legacyNavigation && typeof legacyNavigation.type === 'number') {
      const reloadType =
        typeof legacyNavigation.TYPE_RELOAD === 'number' ? legacyNavigation.TYPE_RELOAD : 1;
      return legacyNavigation.type === reloadType ? 'reload' : 'not-reload';
    }
    return 'unknown';
  } catch (error) {
    return 'unknown';
  }
}

/**
 * Decide whether an automatic forced reload may proceed for this target.
 * An automatic reload is permitted only when the chain it could start is
 * provably bounded, that is when we can EITHER durably record the attempt
 * (so the page after the reload sees it and refuses) OR positively confirm
 * this page is a fresh navigation and not itself a post-reload page.
 * Bounds, in order:
 * - never twice for the same target identity in one tab session;
 * - never more than MAX_AUTO_RELOADS_PER_SESSION distinct targets, so
 *   manifest churn cannot cycle reloads;
 * - when sessionStorage is denied (the record cannot survive the reload),
 *   the in-memory record bounds this page to one automatic reload, only a
 *   CONFIRMED fresh navigation may reload, a page that is itself already a
 *   reload never auto-reloads again, and an indeterminate navigation type
 *   never reloads at all: the persistent prompt shows instead.
 */
function shouldAutoReload(identity: string): boolean {
  const attempts = getReloadAttempts();
  if (attempts.includes(identity)) {
    return false;
  }
  if (attempts.length >= MAX_AUTO_RELOADS_PER_SESSION) {
    return false;
  }

  if (!inMemoryReloadAttempts.includes(identity)) {
    inMemoryReloadAttempts.push(identity);
  }
  if (persistReloadAttempts([...attempts, identity])) {
    return true;
  }
  // The attempt could not be durably recorded, so nothing survives a reload
  // to stop the next page doing this again. Reload only when navigation
  // timing POSITIVELY confirms this page is not itself a post-reload page;
  // 'reload' and 'unknown' both fall back to the persistent prompt.
  return classifyCurrentLoad() === 'not-reload';
}

/**
 * Check whether the server is publishing a different build than the one
 * running. Detection is pure comparison: no storage is read or written, so
 * a pending update keeps being reported until the running bundle changes.
 */
export async function checkForUpdates(): Promise<UpdateCheckResult> {
  const noUpdate: UpdateCheckResult = {
    updateAvailable: false,
    forceUpdate: false,
    pendingVersion: null,
  };

  const serverVersion = await fetchServerVersion();
  if (!serverVersion) {
    return noUpdate;
  }

  const runningVersion = getRunningVersion();
  if (!runningVersion) {
    return noUpdate;
  }

  if (!isDifferentBuild(serverVersion, runningVersion)) {
    return noUpdate;
  }

  return {
    updateAvailable: true,
    forceUpdate: Boolean(serverVersion.forceUpdate),
    pendingVersion: serverVersion,
  };
}

/**
 * Perform a hard reload to get the latest version
 */
export function performHardReload(): void {
  // Clear all caches
  if ('caches' in window) {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  }

  // Unregister service workers
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        registration.unregister();
      });
    });
  }

  // Wait a moment for cleanup, then hard reload
  setTimeout(() => {
    window.location.reload();
  }, 100);
}

/**
 * Start periodic version checking.
 *
 * Non-forced deployments notify the caller so it can show a dismissible
 * prompt; because detection is anchored on the running bundle, a dismissed
 * prompt resurfaces on the next check. Forced deployments reload
 * automatically within the shouldAutoReload bounds; when a reload is not
 * allowed (already attempted, cap reached, or storage-denied brake), the
 * caller is notified with forced set so it can show a persistent prompt
 * instead of looping reloads.
 */
export function startVersionChecking(
  onUpdateAvailable?: (prompt: UpdatePrompt) => void
): () => void {
  let stopped = false;
  // In-page guard: never trigger more than one automatic reload per page
  // lifetime, even if a periodic check races the pending reload.
  let reloadInitiated = false;

  const checkAndNotify = async () => {
    const result = await checkForUpdates();
    if (stopped || reloadInitiated || !result.updateAvailable || !result.pendingVersion) {
      return;
    }

    if (result.forceUpdate) {
      if (shouldAutoReload(targetIdentity(result.pendingVersion))) {
        reloadInitiated = true;
        performHardReload();
        return;
      }
      // An automatic reload is not allowed for this target any more.
      // Fall through to the persistent prompt instead of reloading again.
    }

    if (onUpdateAvailable) {
      onUpdateAvailable({
        pendingVersion: result.pendingVersion,
        forced: result.forceUpdate,
      });
    }
  };

  // Check immediately
  checkAndNotify();

  // Set up periodic checking
  const intervalId = setInterval(checkAndNotify, VERSION_CHECK_INTERVAL);

  // Return cleanup function
  return () => {
    stopped = true;
    clearInterval(intervalId);
  };
}
